import React from 'react';
import { Anchor, Button, PasswordInput, Stack, Text } from '@mantine/core';
import { IconFingerprint } from '@tabler/icons-react';
import FlowAlert from '@/components/auth/flow/components/parts/Flow.Alert';
import passwordAcceptable, { PASSWORD_RULE_LABEL } from '@/function/auth/password.acceptable';
import { BEAUTH_PASSWORD_MAX } from '@/config/beauth.config';
import type { TypeFlowScreenProps } from '@/types/auth/types.auth.flow';

type Props = TypeFlowScreenProps & { step: string; passkeyStep: string | null; passkeySupported: boolean; submitLabel?: string };

const FlowNewPassword: React.FC<Props> = ({ flow, message, step, passkeyStep, passkeySupported, submitLabel = 'SALVA LA PASSWORD' }) => {
    const [password, setPassword] = React.useState('');
    const [confirm, setConfirm] = React.useState('');
    const [errors, setErrors] = React.useState<{ password?: string; confirm?: string }>({});
    const serverError = message?.field === 'password' ? message.text : null;

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (flow.busy) return;
        const next: { password?: string; confirm?: string } = {};
        if (!passwordAcceptable(password)) next.password = `La password non va bene: ${PASSWORD_RULE_LABEL.toLowerCase()}.`;
        if (!confirm || confirm !== password) next.confirm = 'Le password non coincidono';
        setErrors(next);
        if (next.password || next.confirm) return;
        void flow.submit(step, { password });
    };

    return (
        <form onSubmit={submit} noValidate>
            <Stack gap="15px">
                <FlowAlert message={message} field="password" />
                {flow.hint ? <input type="email" name="username" autoComplete="username" hidden readOnly value={flow.hint} /> : null}
                <PasswordInput
                    withAsterisk
                    label="Nuova password"
                    description={PASSWORD_RULE_LABEL}
                    placeholder="La nuova password"
                    autoComplete="new-password"
                    maxLength={BEAUTH_PASSWORD_MAX}
                    data-autofocus
                    value={password}
                    error={errors.password ?? serverError ?? undefined}
                    onChange={(event) => {
                        setErrors((current) => ({ ...current, password: undefined }));
                        setPassword(event.currentTarget.value.slice(0, BEAUTH_PASSWORD_MAX));
                    }}
                />
                <PasswordInput
                    withAsterisk
                    label="Conferma la password"
                    placeholder="Ripeti la nuova password"
                    autoComplete="new-password"
                    maxLength={BEAUTH_PASSWORD_MAX}
                    value={confirm}
                    error={errors.confirm}
                    onChange={(event) => {
                        setErrors((current) => ({ ...current, confirm: undefined }));
                        setConfirm(event.currentTarget.value.slice(0, BEAUTH_PASSWORD_MAX));
                    }}
                />
                <Button type="submit" radius="md" loading={flow.busy} disabled={flow.busy}>{submitLabel}</Button>
                {passkeyStep && passkeySupported ? (
                    <Stack gap={4} align="center">
                        <Text size="sm" c="dimmed" ta="center">Oppure entra senza password, con l’impronta o il volto del dispositivo.</Text>
                        <Anchor component="button" type="button" size="sm" disabled={flow.busy} style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => flow.choose('passkey')}>
                            <IconFingerprint size={16} aria-hidden />
                            Crea una passkey
                        </Anchor>
                    </Stack>
                ) : null}
            </Stack>
        </form>
    );
};

export default FlowNewPassword;
